import type { NatRuleColumn } from '@duke-hq/avetmiss/NATTypes';

/**
 * Shared sizing/padding for NAT report table cells.
 *
 * Headers and body cells must agree on these values or the columns visibly misalign.
 * Width is a fixed-pixel estimate based on character count plus a reserve for the
 * inline error icon shown next to invalid cell values.
 */

const CELL_CHAR_WIDTH_PX = 10;
const CELL_ICON_WIDTH_PX = 16;
const CELL_ICON_MARGIN_PX = 8;
const CELL_ICON_GAP_PX = 16;

const CELL_ICON_RESERVE_PX = CELL_ICON_WIDTH_PX + CELL_ICON_MARGIN_PX + CELL_ICON_GAP_PX;

/** Horizontal padding inside each cell — kept small so narrow columns can fit a 4-char value next to an icon. */
export const NAT_CELL_PADDING_X = 3;
export const NAT_CELL_PADDING_Y = 4;

/**
 * Content-sized width for a NAT column: char-count estimate + reserve for the inline icon.
 */
export function getNatColumnContentWidth(column: NatRuleColumn | undefined): string {
  const charBased = Math.max(
    (column?.name?.length ?? 0) * CELL_CHAR_WIDTH_PX,
    (column?.size ?? 0) * CELL_CHAR_WIDTH_PX,
  );
  return `${charBased + CELL_ICON_RESERVE_PX}px`;
}

/**
 * Width for a NAT table column. The last column stretches to fill the container;
 * everything else uses its content-sized width.
 */
export function getNatColumnWidth(
  column: NatRuleColumn | undefined,
  index: number,
  totalColumns: number,
): string {
  return index === totalColumns - 1 ? '100%' : getNatColumnContentWidth(column);
}
